<script lang="ts">
  /* SearchSelect — a button that opens a panel with a search input and a
     filtered option list. The input is auto-focused on open and filtering
     only kicks in once the query reaches 2 characters (before that the full
     list is shown). Used for the Orders counterparty filter and the
     create-order client picker. */
  import { _ } from "svelte-i18n";

  type Option = { value: string; label: string; sub?: string; group?: string };

  let {
    options,
    value = $bindable(""),
    placeholder = "",
    searchPlaceholder = "",
    allLabel = "",
    disabled = false,
    groupRank = () => 0,
    onchange = (_v: string) => {},
  }: {
    options: Option[];
    value?: string;
    placeholder?: string;
    searchPlaceholder?: string;
    /** When set, an "all" pseudo-option (value "") is shown at the top. */
    allLabel?: string;
    disabled?: boolean;
    /** Sort weight per group name — lower sorts first, ties break
     *  alphabetically. Ignored when options carry no `group`. */
    groupRank?: (group: string) => number;
    onchange?: (v: string) => void;
  } = $props();

  let open = $state(false);
  let query = $state("");
  let wrap = $state<HTMLElement | null>(null);
  let inputEl = $state<HTMLInputElement | null>(null);

  const selectedLabel = $derived(
    value ? (options.find((o) => o.value === value)?.label ?? value) : (allLabel || placeholder),
  );

  // Filters from the very first character: on a ~250-entry list (countries)
  // waiting for a second letter means the list has not visibly reacted to
  // typing at all.
  const filtered = $derived.by(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || (o.sub ?? "").toLowerCase().includes(q),
    );
  });

  /* Grouped view of `filtered`, mirroring the region sections in Partner search
     so a 250-country list reads as a handful of continents rather than one wall
     of names. Only kicks in when the caller supplies `group`; ungrouped callers
     (counterparties, clients) render exactly as before.

     Groups are not collapsible here: the panel already has a search box, and a
     picker that needs two clicks before showing any country would be worse than
     the flat list it replaced. */
  const groupedOptions = $derived.by(() => {
    if (!options.some((o) => o.group)) return null;
    const m = new Map<string, Option[]>();
    for (const o of filtered) {
      const key = o.group || "-";
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(o);
    }
    return [...m.entries()].sort((a, b) => {
      const ra = groupRank(a[0]), rb = groupRank(b[0]);
      return ra !== rb ? ra - rb : a[0].localeCompare(b[0]);
    });
  });

  function toggle() {
    if (disabled) return;
    open = !open;
    if (open) {
      query = "";
      // Focus the input as soon as the panel renders.
      queueMicrotask(() => inputEl?.focus());
    }
  }

  function choose(v: string) {
    value = v;
    open = false;
    onchange(v);
  }

  function onWindowClick(e: MouseEvent) {
    if (open && wrap && !wrap.contains(e.target as Node)) open = false;
  }
  function onKey(e: KeyboardEvent) {
    if (e.key === "Escape") open = false;
  }
</script>

<svelte:window onclick={onWindowClick} onkeydown={onKey} />

<div class="ss-wrap" bind:this={wrap}>
  <button
    type="button"
    class="ss-trigger"
    class:placeholder={!value && !allLabel}
    {disabled}
    aria-haspopup="listbox"
    aria-expanded={open}
    onclick={toggle}
  >
    <span class="ss-trigger-label">{selectedLabel}</span>
    <span class="material-icons-outlined ss-caret">{open ? "expand_less" : "expand_more"}</span>
  </button>

  {#if open}
    <div class="ss-panel" role="listbox">
      <div class="ss-search">
        <span class="material-icons-outlined">search</span>
        <input
          bind:this={inputEl}
          bind:value={query}
          type="text"
          placeholder={searchPlaceholder || $_("common.search")}
        />
      </div>
      <ul class="ss-options">
        {#if allLabel}
          <li>
            <button type="button" class="ss-option" class:active={!value} onclick={() => choose("")}>
              {allLabel}
            </button>
          </li>
        {/if}
        {#if groupedOptions}
          {#each groupedOptions as [group, items] (group)}
            <li class="ss-group">{group}</li>
            {#each items as opt (opt.value)}
              <li>
                <button
                  type="button"
                  class="ss-option"
                  class:active={value === opt.value}
                  onclick={() => choose(opt.value)}
                >
                  <span class="ss-option-label">{opt.label}</span>
                  {#if opt.sub}<span class="ss-option-sub">{opt.sub}</span>{/if}
                </button>
              </li>
            {/each}
          {/each}
        {:else}
          {#each filtered as opt (opt.value)}
            <li>
              <button
                type="button"
                class="ss-option"
                class:active={value === opt.value}
                onclick={() => choose(opt.value)}
              >
                <span class="ss-option-label">{opt.label}</span>
                {#if opt.sub}<span class="ss-option-sub">{opt.sub}</span>{/if}
              </button>
            </li>
          {/each}
        {/if}
        {#if filtered.length === 0}
          <li class="ss-empty">{$_("common.noResults")}</li>
        {/if}
      </ul>
    </div>
  {/if}
</div>

<style>
  .ss-wrap { position: relative; display: inline-block; }
  .ss-trigger {
    display: inline-flex; align-items: center; gap: 6px; justify-content: space-between;
    min-width: 180px; max-width: 240px; padding: 7px 10px; background: #fff;
    border: 1px solid var(--color-divider); border-radius: var(--radius);
    font-size: 13px; font-family: inherit; cursor: pointer; color: var(--color-surface-darker);
  }
  .ss-trigger:hover:not(:disabled) { border-color: var(--color-primary); }
  .ss-trigger:disabled { opacity: 0.5; cursor: default; }
  .ss-trigger.placeholder { color: var(--color-text-muted); }
  .ss-trigger-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .ss-caret { font-size: 18px; color: var(--color-text-muted); flex-shrink: 0; }

  .ss-panel {
    position: absolute; top: calc(100% + 4px); left: 0; z-index: 200;
    min-width: 240px; max-width: min(320px, calc(100vw - 32px)); background: #fff;
    border: 1px solid var(--color-divider); border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.16); overflow: hidden;
  }
  .ss-search { display: flex; align-items: center; gap: 6px; padding: 8px 10px; border-bottom: 1px solid var(--color-divider); }
  .ss-search .material-icons-outlined { font-size: 18px; color: var(--color-text-muted); }
  .ss-search input { flex: 1; border: none; outline: none; font-size: 13px; font-family: inherit; }
  .ss-options { list-style: none; margin: 0; padding: 4px 0; max-height: 280px; overflow-y: auto; }
  .ss-option {
    display: flex; flex-direction: column; gap: 1px; width: 100%; text-align: left;
    padding: 8px 12px; border: none; background: none; cursor: pointer;
    font-size: 13px; font-family: inherit; color: var(--color-surface-darker);
  }
  .ss-option:hover { background: #f5f8fc; }
  .ss-option.active { background: rgba(33,133,213,0.1); font-weight: 600; }
  .ss-option-sub { font-size: 11px; color: var(--color-text-muted); }
  /* Sticky so the continent you're scrolling through stays named. */
  .ss-group {
    position: sticky; top: 0; z-index: 1;
    padding: 6px 12px 4px; background: #f7f9fb;
    border-bottom: 1px solid var(--color-divider);
    font-size: 10px; font-weight: 700; letter-spacing: 0.06em;
    text-transform: uppercase; color: var(--color-text-muted);
  }
  .ss-empty { padding: 10px 12px; font-size: 12px; color: var(--color-text-muted); text-align: center; }
</style>
